import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsPartnerFeatureFlags, useGetPartnerAdminsUser } from '~/hooks';
import { useConfiguration } from '~/contexts/ConfigurationProvider';
import { getUserLink } from '~/utils/getLinks';
import { convertToFormValues, generateRequest, resolveFieldRequirements } from '../logics';
import { createEditUserFormSchema, type EditUserFormFieldValues } from '../logics/schema';
import type { EditUserFormPresentation } from '../presentations';
import { usePutPartnerAdminsUser } from './usePutPartnerAdminsUser';

type PresentationProps = React.ComponentProps<typeof EditUserFormPresentation>;

type IUseEditUserForm = (userId: number) => {
  state: {
    data: ReturnType<typeof useGetPartnerAdminsUser>['data'];
    methods: UseFormReturn<EditUserFormFieldValues>;
    isButtonDisabled: boolean;
    isEmailRequired: PresentationProps['isEmailRequired'];
    isPhoneNumberFieldVisible: PresentationProps['isPhoneNumberFieldVisible'];
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * 利用者編集フォームのカスタムフック
 *
 * メールアドレス必須/電話番号フィールド表示の判定は、activation_code PartnerFeatureFlag
 * （useGetPartnerAdminsPartnerFeatureFlags）× login_method（useConfiguration）×
 * 編集対象利用者の is_activated（useGetPartnerAdminsUser）から導出する。
 * この hook は各 API/Context から取得した生データをそのまま
 * logics/resolveFieldRequirements に渡すだけで、値の取り出し（activation_code フラグの
 * 判定・login_method / is_activated の取り出し）は関数内に閉じ込めている。
 *
 * @param userId - 編集対象の利用者ID
 * @returns state.data - 利用者データ
 * @returns state.methods - react-hook-form のメソッド
 * @returns state.isButtonDisabled - 保存ボタン非活性フラグ
 * @returns state.isEmailRequired - メールアドレスが必須かどうか
 * @returns state.isPhoneNumberFieldVisible - 電話番号フィールドの表示可否
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useEditUserForm: IUseEditUserForm = userId => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data } = useGetPartnerAdminsUser({ threeLetterCode, userId });
  const { data: partnerFeatureFlagData } = useGetPartnerAdminsPartnerFeatureFlags({
    threeLetterCode,
  });
  const { applicationConfiguration } = useConfiguration();

  // zodResolver に渡す schema（createEditUserFormSchema）を生成するために
  // isEmailRequired / requireEitherEmailOrPhoneNumber が必要なため、ここで resolveFieldRequirements を呼び出す。
  // 併せて電話番号フィールドの表示制御（Presentation）・generateRequest で使う isPhoneNumberFieldVisible も導出する。
  const { isEmailRequired, isPhoneNumberFieldVisible, requireEitherEmailOrPhoneNumber } =
    resolveFieldRequirements({ partnerFeatureFlagData, applicationConfiguration, data });

  const methods = useForm<EditUserFormFieldValues>({
    defaultValues: convertToFormValues(data),
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(
      createEditUserFormSchema(isEmailRequired, requireEitherEmailOrPhoneNumber)
    ),
  });

  const { trigger, isMutating } = usePutPartnerAdminsUser(threeLetterCode, userId, {
    onSuccess: () => {
      showToast({
        message: '利用者情報を保存しました。',
        status: 'success',
      });
      navigate(getUserLink(userId));
    },
    onError: error => {
      const errors = error.response?.data?.errors;
      showToast({
        message: errors?.[0] ?? '利用者情報の更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formValues => {
    trigger(generateRequest(formValues, isPhoneNumberFieldVisible));
  });

  return {
    state: {
      data,
      methods,
      isButtonDisabled: methods.formState.isSubmitting || isMutating,
      isEmailRequired,
      isPhoneNumberFieldVisible,
    },
    actions: { handleOnSubmit },
  };
};
